import { useEffect, useMemo, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";

import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import BaseMedCardList from "src/components/shared-components/medcard/BaseMedCardList";
import NewBornFollow from "../create";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { referenceService } from "src/api/references/referenceService";

type NewbornRow = {
  id: string | number;
  head_circumference?: string | number | null;
  waist_circumference?: string | number | null;
  incubator_number?: string | number | null;
  incubator_change_date?: string | null;
  incubator_temperature?: string | number | null;
  incubator_damp?: string | number | null;
  is_it_phototherapy_done?: string | null;
  rop_date?: string | null;
  hearing_test_date?: string | null;
  echocardiography_date?: string | null;
  heel_blood_date?: string | null;
  write_on_epicrise?: string | null;
  ptime?: string | null;
  cuser?: string | null;
  ctime?: string | null;
  pt_id?: number | null;
  upn?: number | null;
};

function NewBornFollowList() {
  const { t } = useTranslate();
  const { id: routePatientId } = useParams();
  const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
  const selectedPatientId = mainTabs?.[activeTabIndex]?.data?.patientId;
  const selectedUpn = mainTabs?.[activeTabIndex]?.data?.upn;

  const patientIdNumber = useMemo(
    () => Number(routePatientId || selectedPatientId || 0),
    [routePatientId, selectedPatientId]
  );

  const [rows, setRows] = useState<NewbornRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState<number | null>(null);
  const [editInitialData, setEditInitialData] = useState<any>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");

  const loadData = useCallback(async () => {
    if (!patientIdNumber && !selectedUpn) return;
    try {
      setLoading(true);
      const response = await referenceService.newbornTrace.get({
        PtId: patientIdNumber || undefined,
        UPN: selectedUpn || undefined,
        search_date: dayjs().format("DD.MM.YYYY"),
        all_recs: 1,
      });

      const raw = (response as any)?.data ?? response;
      const parsed =
        typeof raw === "string" ? JSON.parse(raw) : raw?.data ?? raw;

      if (Array.isArray(parsed)) {
        const mapped = parsed.map((item: any) => ({
          id: item.id ?? item.ID,
          head_circumference: item.head_circumference ?? item.HEAD_CIRCUMFERENCE ?? null,
          waist_circumference: item.waist_circumference ?? item.WAIST_CIRCUMFERENCE ?? null,
          incubator_number: item.incubator_number ?? item.INCUBATOR_NUMBER ?? null,
          incubator_change_date: item.incubator_change_date ?? item.INCUBATOR_CHANGE_DATE ?? null,
          incubator_temperature: item.incubator_temperature ?? item.INCUBATOR_TEMPERATURE ?? null,
          incubator_damp: item.incubator_damp ?? item.INCUBATOR_DAMP ?? null,
          is_it_phototherapy_done: item.is_it_phototherapy_done ?? item.IS_IT_PHOTOTHERAPY_DONE ?? null,
          rop_date: item.rop_date ?? item.ROP_DATE ?? null,
          hearing_test_date: item.hearing_test_date ?? item.HEARING_TEST_DATE ?? null,
          echocardiography_date: item.echocardiography_date ?? item.ECHOCARDIOGRAPHY_DATE ?? null,
          heel_blood_date: item.heel_blood_date ?? item.HEEL_BLOOD_DATE ?? null,
          write_on_epicrise: item.write_on_epicrise ?? item.WRITE_ON_EPICRISE ?? null,
          ptime: item.ptime ?? item.PTIME ?? null,
          cuser: item.cuser ?? item.CUSER ?? null,
          ctime: item.ctime ?? item.CTIME ?? null,
          pt_id: item.pt_id ?? item.PT_ID ?? null,
          upn: item.upn ?? item.UPN ?? null,
        }));
        setRows(mapped);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("GetNewbornTrace error:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [patientIdNumber, selectedUpn]);

  const handleDeleteOpen = useCallback((id: number) => {
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteTargetId(null);
  }, []);

  const handleDeleteConfirm = useCallback(
    async (): Promise<boolean> => {
      if (!deleteTargetId) return false;
      try {
        await referenceService.newbornTrace.delete(deleteTargetId);
        await loadData();
        setAlertSeverity("success");
        setAlertMessage(t("card.record-deleted-successfully") || "Tıbbi kart başarıyla silindi");
        setAlertOpen(true);
        return true;
      } catch (error) {
        console.error("DeleteNewbornTrace error:", error);
        setAlertSeverity("error");
        setAlertMessage(t("card.delete-failed") || "Silme işlemi başarısız");
        setAlertOpen(true);
        return false;
      }
    },
    [deleteTargetId, loadData, t]
  );

  const handleEdit = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const raw = await referenceService.newbornTrace.getById(id);
      const normalize = (item: any) => ({
        id: item.id ?? item.ID,
        head_circumference: item.head_circumference ?? item.HEAD_CIRCUMFERENCE ?? null,
        waist_circumference: item.waist_circumference ?? item.WAIST_CIRCUMFERENCE ?? null,
        incubator_number: item.incubator_number ?? item.INCUBATOR_NUMBER ?? null,
        incubator_change_date: item.incubator_change_date ?? item.INCUBATOR_CHANGE_DATE ?? null,
        incubator_temperature: item.incubator_temperature ?? item.INCUBATOR_TEMPERATURE ?? null,
        incubator_damp: item.incubator_damp ?? item.INCUBATOR_DAMP ?? null,
        is_it_phototherapy_done: item.is_it_phototherapy_done ?? item.IS_IT_PHOTOTHERAPY_DONE ?? null,
        rop_date: item.rop_date ?? item.ROP_DATE ?? null,
        hearing_test_date: item.hearing_test_date ?? item.HEARING_TEST_DATE ?? null,
        echocardiography_date: item.echocardiography_date ?? item.ECHOCARDIOGRAPHY_DATE ?? null,
        heel_blood_date: item.heel_blood_date ?? item.HEEL_BLOOD_DATE ?? null,
        write_on_epicrise: item.write_on_epicrise ?? item.WRITE_ON_EPICRISE ?? null,
        ptime: item.ptime ?? item.PTIME ?? null,
        pt_id: item.pt_id ?? item.PT_ID ?? null,
        upn: item.upn ?? item.UPN ?? null,
        cuser: item.cuser ?? item.CUSER ?? null,
        ctime: item.ctime ?? item.CTIME ?? null,
      });
      setEditInitialData(normalize(raw));
      setEditRecordId(id);
      setEditModalOpen(true);
    } catch (error) {
      console.error("GetNewbornTraceById error:", error);
      setAlertSeverity("error");
      setAlertMessage(t("card.save-error") || "Kayıt yüklenirken hata oluştu.");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditRecordId(null);
    setEditInitialData(null);
  }, []);

  const handleEditSaved = useCallback(() => {
    handleCloseEditModal();
    loadData();
  }, [handleCloseEditModal, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = useCallback((value?: string | null) => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD.MM.YYYY HH:mm") : "-";
  }, []);

  const columns: GridColDef<NewbornRow>[] = useMemo(
    () => [
      {
        field: "edit",
        headerName: t("buttons.edit") || "Düzenle",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          if (!params?.row) return null;
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
              <ByzGrdEditButton
                onClick={() => handleEdit(Number(params.row.id))}
              />
            </Box>
          );
        },
      },
      {
        field: "ptime",
        headerName: t("height-weight.date") || "Tarih",
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.ptime
            ? dayjs(row.ptime).format("DD.MM.YYYY HH:mm")
            : "-";
        },
      },
      {
        field: "head_circumference",
        headerName: (() => {
          const lbl = t("newborn-follow.head-circumference");
          return lbl === "newborn-follow.head-circumference" ? "Baş çevresi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.head_circumference ?? "-";
        },
      },
      {
        field: "waist_circumference",
        headerName: (() => {
          const lbl = t("newborn-follow.waist-circumference");
          return lbl === "newborn-follow.waist-circumference" ? "Bel çevresi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.waist_circumference ?? "-";
        },
      },
      {
        field: "incubator_number",
        headerName: (() => {
          const lbl = t("newborn-follow.incubator-number");
          return lbl === "newborn-follow.incubator-number" ? "Kuvöz no" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.incubator_number ?? "-";
        },
      },
      {
        field: "incubator_change_date",
        headerName: (() => {
          const lbl = t("newborn-follow.incubator-change-date");
          return lbl === "newborn-follow.incubator-change-date" ? "Kuvöz değişim tarihi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return formatDate(row.incubator_change_date ?? null);
        },
      },
      {
        field: "incubator_temperature",
        headerName: (() => {
          const lbl = t("newborn-follow.incubator-temperature");
          return lbl === "newborn-follow.incubator-temperature" ? "Kuvöz ısısı" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.incubator_temperature ?? "-";
        },
      },
      {
        field: "incubator_damp",
        headerName: (() => {
          const lbl = t("newborn-follow.incubator-damp");
          return lbl === "newborn-follow.incubator-damp" ? "Kuvöz nemi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.incubator_damp ?? "-";
        },
      },
      {
        field: "is_it_phototherapy_done",
        headerName: (() => {
          const lbl = t("newborn-follow.phototherapy");
          return lbl === "newborn-follow.phototherapy" ? "Fototerapi" : lbl;
        })(),
        width: 140,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.is_it_phototherapy_done === "1"
            ? t("yes") || "Evet"
            : t("no") || "Hayır";
        },
      },
      {
        field: "rop_date",
        headerName: (() => {
          const lbl = t("newborn-follow.rop-date");
          return lbl === "newborn-follow.rop-date" ? "ROP tarihi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return formatDate(row.rop_date ?? null);
        },
      },
      {
        field: "hearing_test_date",
        headerName: (() => {
          const lbl = t("newborn-follow.hearing-test-date");
          return lbl === "newborn-follow.hearing-test-date" ? "İşitme test tarihi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return formatDate(row.hearing_test_date ?? null);
        },
      },
      {
        field: "echocardiography_date",
        headerName: (() => {
          const lbl = t("newborn-follow.echo-date");
          return lbl === "newborn-follow.echo-date" ? "Eko tarihi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return formatDate(row.echocardiography_date ?? null);
        },
      },
      {
        field: "heel_blood_date",
        headerName: (() => {
          const lbl = t("newborn-follow.heel-blood-date");
          return lbl === "newborn-follow.heel-blood-date" ? "Topuk kanı (FKU) tarihi" : lbl;
        })(),
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return formatDate(row.heel_blood_date ?? null);
        },
      },
      {
        field: "write_on_epicrise",
        headerName: (() => {
          const lbl = t("height-weight.write-on-epicrise");
          return lbl === "height-weight.write-on-epicrise" ? "Epikrizde çıksın" : lbl;
        })(),
        width: 160,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.write_on_epicrise === "1"
            ? t("yes") || "Evet"
            : t("no") || "Hayır";
        },
      },
      {
        field: "cuser",
        headerName: t("height-weight.user") || t("user") || "Kullanıcı",
        flex: 1,
        valueGetter: (value, row) => {
          if (!row) return "-";
          return row.cuser ?? "-";
        },
      },
      {
        field: "delete",
        headerName: t("buttons.delete") || "Sil",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          if (!params?.row) return null;
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
              <ByzGrdDeleteButton
                onClick={() => handleDeleteOpen(Number(params.row.id))}
              />
            </Box>
          );
        },
      },
    ],
    [t, handleEdit, handleDeleteOpen]
  );

  return (
    <>

      <BaseMedCardList
        title={t("nurse.patient-list.detail-grid.newborn-trace") || "Yenidoğan Takip"}
        columns={columns}
        rows={rows}
        CreateComponent={NewBornFollow}
        createComponentProps={{
          onSaved: (action?: "add" | "update") => {
            loadData();
            setAlertSeverity("success");
            setAlertMessage(
              action === "update"
                ? t("card.record-updated-successfully") || "Tıbbi kart başarıyla güncellendi"
                : t("card.record-added-successfully") || "Tıbbi kart başarıyla kaydedildi"
            );
            setAlertOpen(true);
          },
          onError: () => {
            setAlertSeverity("error");
            setAlertMessage(t("card.save-error") || "Kayıt işlemi başarısız");
            setAlertOpen(true);
          },
        }}
        pageSize={10}
        customToolbar={undefined}
        extraActions={undefined}
        onModalClose={loadData}
      />
      <OpenComponentDialog
        title={t("nurse.patient-list.detail-grid.newborn-trace") || "Yenidoğan Takip"}
        open={editModalOpen}
        onClose={handleCloseEditModal}
        isMedCard={true}
      >
        {editInitialData && (
          <NewBornFollow
            mode="edit"
            recordId={editRecordId}
            initialData={editInitialData}
            onSaved={(action?: "add" | "update") => {
              handleEditSaved();
              setAlertSeverity("success");
              setAlertMessage(
                action === "update"
                  ? t("card.record-updated-successfully") || "Tıbbi kart başarıyla güncellendi"
                  : t("card.record-added-successfully") || "Tıbbi kart başarıyla kaydedildi"
              );
              setAlertOpen(true);
            }}
            onError={() => {
              setAlertSeverity("error");
              setAlertMessage(t("card.save-error") || "Kayıt işlemi başarısız");
              setAlertOpen(true);
            }}
            onCloseModal={handleCloseEditModal}
          />
        )}
      </OpenComponentDialog>
      <ByzDeleteConfirmDialog
        open={deleteModalOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        patientId={deleteTargetId?.toString()}
        title={t("delete-confirm.title") || "Silme Onayı"}
        content={t("delete-confirm.content") || "Bu kaydı silmek istediğinize emin misiniz?"}
        successMessage={t("card.record-deleted-successfully") || "Tıbbi kart başarıyla silindi"}
        errorMessage={t("card.delete-failed") || "Silme işlemi başarısız"}
        hideSnackbar={true}
        onSuccess={(success) => {
          if (success) {
            handleDeleteClose();
          }
        }}
      />
      <ByzMuiSnackBar
        open={alertOpen}
        type={alertSeverity}
        title={
          alertSeverity === "error"
            ? t("common.error") || "Hata"
            : t("common.success") || t("buttons.success") || "Başarılı"
        }
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
}

export default NewBornFollowList;
